import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from 'react-dropdown';
import tinycolor from 'tinycolor2';
import 'react-dropdown/style.css';

import { joinClassNames } from '../../util/util';
import { useShopping } from '../../contexts/ShoppingContext';
import { usePlayer } from '../../contexts/PlayerContext';
import { useConfiguration } from '../../contexts/ConfigurationContext';

import './ShoppingBasket.scss';

interface ShoppingBasketProps {
	settings: any;
	isMobile?: boolean;
}

interface CartProduct {
	productId: string;
	productPage: string;
	name: string;
	price: string;
	quantity: string;
	description: string;
	image: string;
	variantText1?: string;
	variantValue1?: string;
	variantText2?: string;
	variantValue2?: string;
}

interface VariantCombination {
	comboId: string;
	comboName: string;
	comboPrice: string;
	comboImg: string;
}

interface PrebasketProduct {
	prodId?: { value: string };
	prodPage?: { value: string };
	prodName?: { value: string };
	price?: { value: string };
	prodDescription?: { value: string };
	imageUrl?: { value: string };
	hideVolumeCounter?: { value: boolean };
	variantName1?: { value: string };
	variantName2?: { value: string };
	variantValues1?: { value: string[] };
	variantValues2?: { value: string[] };
	variantCombinations?: { value: VariantCombination[] };
}

interface CurrencyValues {
	EUR: number;
	SEK: number;
	DKK: number;
	NOK: number;
	USD: number;
	GBP: number;
}

type TabType = 'cart' | 'list' | 'prebasket';

const ShoppingBasket: React.FC<ShoppingBasketProps> = ({ settings, isMobile }) => {
	const { t } = useTranslation();
	const { showShoppingCart, setShowShoppingCart } = usePlayer();
	const {
		cartArray,
		setCartArray,
		listArray,
		currentPrebasketProduct,
		setCurrentPrebasketProduct,
		prebasketUpdated,
		setPreBasketUpdated,
	} = useShopping();
	const { mediasData, currentMediaId } = useConfiguration();

	const [currentTab, setCurrentTab] = useState<TabType>('cart');
	const [prebasketProduct, setPrebasketProduct] = useState<PrebasketProduct>({});
	const [currentSliderIndex, setCurrentSliderIndex] = useState(0);
	const [currentComboIndex, setCurrentComboIndex] = useState(0);
	const [currentDropdownValue1, setCurrentDropdownValue1] = useState('');
	const [currentDropdownValue2, setCurrentDropdownValue2] = useState('');

	const currentMedia = useMemo(() => mediasData.find((media: any) => media?.id === currentMediaId), [mediasData, currentMediaId]);

	useEffect(() => {
		if (!showShoppingCart) {
			setCurrentTab('cart');
		}
	}, [showShoppingCart]);

		useEffect(() => {
		if (prebasketUpdated && Object.keys(currentPrebasketProduct).length > 0) {
			setCurrentTab('prebasket');
			setPrebasketProduct(currentPrebasketProduct);
			setCurrentComboIndex(0);
			setCurrentDropdownValue1(currentPrebasketProduct.variantValues1?.value[0] || '');
			setCurrentDropdownValue2(currentPrebasketProduct.variantValues2?.value[0] || '');
			setPreBasketUpdated(false);
		}
	}, [prebasketUpdated, currentPrebasketProduct, setPreBasketUpdated]);	useEffect(() => {
		if (Object.keys(currentPrebasketProduct).length > 0) {
			let tempName = '';
			if (currentPrebasketProduct.variantName1?.value !== '' && currentPrebasketProduct.variantName2?.value !== '') {
				tempName = currentDropdownValue1 + '-' + currentDropdownValue2;
			} else if (currentPrebasketProduct.variantName1?.value !== '' && currentPrebasketProduct.variantName2?.value === '') {
				tempName = currentDropdownValue1;
			} else if (currentPrebasketProduct.variantName1?.value === '' && currentPrebasketProduct.variantName2?.value !== '') {
				tempName = currentDropdownValue2;
			}

			const tempIndex = currentPrebasketProduct.variantCombinations?.value.findIndex((item: any) => item.comboName === tempName);
			if (tempIndex !== undefined && tempIndex !== -1) {
				setCurrentComboIndex(tempIndex);
			}
		}
	}, [currentDropdownValue1, currentDropdownValue2, currentPrebasketProduct]);

	useEffect(() => {
		setCurrentSliderIndex(currentComboIndex);
	}, [currentComboIndex]);

	const handleIncreaseQuantity = useCallback(
		(id: string) => {
			const tempArray = [...cartArray];
			const productIndex = tempArray.findIndex((item) => item.productId === id);
			if (productIndex !== -1) {
				tempArray[productIndex] = {
					...tempArray[productIndex],
					quantity: String(Number(tempArray[productIndex].quantity) + 1),
				};
				setCartArray(tempArray);
			}
		},
		[cartArray, setCartArray]
	);

	const handleDecreaseQuantity = useCallback(
		(id: string) => {
			const tempArray = [...cartArray];
			const productIndex = tempArray.findIndex((item) => item.productId === id);
			if (productIndex !== -1 && Number(tempArray[productIndex].quantity) > 1) {
				tempArray[productIndex] = {
					...tempArray[productIndex],
					quantity: String(Number(tempArray[productIndex].quantity) - 1),
				};
				setCartArray(tempArray);
			}
		},
		[cartArray, setCartArray]
	);

	const handleUpdateInputField = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>, id: string) => {
			const tempArray = [...cartArray];
			const productIndex = tempArray.findIndex((item) => item.productId === id);
			if (productIndex !== -1) {
				tempArray[productIndex] = {
					...tempArray[productIndex],
					quantity: event.target.value,
				};
				setCartArray(tempArray);
			}
		},
		[cartArray, setCartArray]
	);

	const handleDeleteItemFromCart = useCallback(
		(id: string) => {
			const tempArray = cartArray.filter((item) => item.productId !== id);
			setCartArray(tempArray);
		},
		[cartArray, setCartArray]
	);

	const handleViewProductButton = useCallback(
		(product: PrebasketProduct) => {
			setCurrentPrebasketProduct(product);
			setPreBasketUpdated(true);
		},
		[setCurrentPrebasketProduct, setPreBasketUpdated]
	);

	const handlePrebasketAddToCart = useCallback(
		(product: PrebasketProduct, variantValue1: string, variantValue2: string) => {
			let obj: Partial<CartProduct> = {};

			if (product.variantName1?.value === '') {
				obj = {
					productId: product.prodId?.value,
					productPage: product.prodPage?.value,
					name: product.prodName?.value,
					price: product.price?.value,
					quantity:
						product.hideVolumeCounter === undefined || (product.hideVolumeCounter !== undefined && product.hideVolumeCounter?.value === false)
							? '1'
							: '0',
					description: product.prodDescription?.value + (product.hideVolumeCounter === undefined ? '' : '(=' + product.hideVolumeCounter?.value),
					image: product.imageUrl?.value,
				};
			} else if (product.variantName2?.value === '') {
				obj = {
					productId: product.prodId?.value,
					productPage: product.prodPage?.value,
					name: product.prodName?.value,
					price: product.price?.value,
					quantity:
						product.hideVolumeCounter === undefined || (product.hideVolumeCounter !== undefined && product.hideVolumeCounter?.value === false)
							? '1'
							: '0',
					description: product.prodDescription?.value + (product.hideVolumeCounter === undefined ? '' : '(=' + product.hideVolumeCounter?.value),
					image: product.imageUrl?.value,
					variantText1: product.variantName1?.value,
					variantValue1: variantValue1,
				};
			} else {
				obj = {
					productId: product.variantCombinations?.value[currentComboIndex]?.comboId,
					productPage: product.prodPage?.value,
					name: product.prodName?.value,
					price: product.variantCombinations?.value[currentComboIndex]?.comboPrice + ' ' + product.price?.value.split(' ')[1],
					quantity:
						product.hideVolumeCounter === undefined || (product.hideVolumeCounter !== undefined && product.hideVolumeCounter?.value === false)
							? '1'
							: '0',
					description: product.prodDescription?.value + (product.hideVolumeCounter === undefined ? '' : '(=' + product.hideVolumeCounter?.value),
					image: product.variantCombinations?.value[currentComboIndex].comboImg,
					variantText1: product.variantName1?.value,
					variantValue1: variantValue1,
					variantText2: product.variantName2?.value,
					variantValue2: variantValue2,
				};
			}

			if (!cartArray.some((el) => el.productId === obj.productId)) {
				setCartArray([...cartArray, obj as CartProduct]);
			}

			setCurrentTab('cart');
			setPrebasketProduct({});
			setCurrentPrebasketProduct({});
		},
		[cartArray, currentComboIndex, setCartArray, setCurrentPrebasketProduct]
	);

	const whichIsTheGreatest = useCallback((numericValues: CurrencyValues): string => {
		if (Math.max(numericValues.EUR, numericValues.SEK, numericValues.DKK, numericValues.NOK, numericValues.USD, numericValues.GBP) === 0) {
			return ' ';
		}

		let max = -Infinity;
		let maxName: string | null = null;

		for (const key in numericValues) {
			const num = numericValues[key as keyof CurrencyValues];
			if (num > max) {
				max = num;
				maxName = key;
			}
		}

		return maxName || ' ';
	}, []);

	const getTotalPrice = useCallback(
		(cartArray: CartProduct[]) => {
			let resultPrice = 0;
			const currencyCount: CurrencyValues = { EUR: 0, SEK: 0, DKK: 0, NOK: 0, USD: 0, GBP: 0 };

			cartArray.forEach((item) => {
				const [priceStr, currency] = item.price.split(' ');
				const price = Number(priceStr);
				const quantity = Number(item.quantity) === 0 ? 1 : Number(item.quantity);

				resultPrice += price * quantity;

				if (currency in currencyCount) {
					currencyCount[currency as keyof CurrencyValues]++;
				}
			});

			return <div className="shoppingBasketCartTotalPriceValue">{resultPrice + ' ' + whichIsTheGreatest(currencyCount)}</div>;
		},
		[whichIsTheGreatest]
	);

	const handleCheckoutButton = useCallback(
		(cartArray: CartProduct[]) => {
			window.parent.postMessage(
				{
					identifier: 'qbrick-shopping',
					shoppingItems: cartArray,
				},
				'*'
			);
			setShowShoppingCart(false);
		},
		[setShowShoppingCart]
	);

	const handleClickNextSlider = useCallback((currentIndex: number, max: number) => {
		setCurrentSliderIndex((prev) => (prev < max - 1 ? prev + 1 : 0));
	}, []);

	const handleClickPrevSlider = useCallback((currentIndex: number, max: number) => {
		setCurrentSliderIndex((prev) => (prev > 0 ? prev - 1 : max - 1));
	}, []);

	const getCurrentSlideMargin = useCallback((slideIndex: number): string => {
		return -Math.abs(slideIndex * 100) + '%';
	}, []);

	const renderCartProduct = useCallback(
		(product: CartProduct) => {
			const primaryBGColor = { backgroundColor: settings?.playerStyling?.primaryColor ?? '#126cfc' };

			const variant1 = product.variantText1 && product.variantValue1 ? `${product.variantText1}: ${product.variantValue1}` : '';
			const variant2 = product.variantText2 && product.variantValue2 ? `${product.variantText2}: ${product.variantValue2}` : '';

			return (
				<div className="shoppingBasketCartSingleProduct" key={product.productId}>
					<div className="shoppingBasketCartSingleProductImageWrapper">
						<img className="shoppingBasketCartSingleProductImage" src={product.image} alt={product.name} />
					</div>
					<div className="shoppingBasketCartSingleProductMiddleSection">
						<a href={product.productPage} target="_blank" className="shoppingBasketCartSingleProductMiddleSectionTitle" rel="noreferrer">
							{product.name}
						</a>
						<div className="shoppingBasketCartSingleProductMiddleSectionPrice">{product.price}</div>
						<div className="shoppingBasketCartSingleProductMiddleSectionVariantWrapper">
							{variant1 && <div className="shoppingBasketCartSingleProductMiddleSectionVariantText">{variant1}</div>}
							{variant2 && <div className="shoppingBasketCartSingleProductMiddleSectionVariantText">{variant2}</div>}
						</div>
						<div className="shoppingBasketCartSingleProductMiddleSectionQuantityWrapper">
							<div
								className="shoppingBasketCartSingleProductMiddleSectionQuantityBtn"
								onClick={() => handleDecreaseQuantity(product.productId)}
								style={primaryBGColor}
							>
								-
							</div>
							<input
								className="shoppingBasketCartSingleProductMiddleSectionQuantityInput"
								type="number"
								onChange={(event) => handleUpdateInputField(event, product.productId)}
								value={product.quantity}
							/>
							<div
								className="shoppingBasketCartSingleProductMiddleSectionQuantityBtn"
								onClick={() => handleIncreaseQuantity(product.productId)}
								style={primaryBGColor}
							>
								+
							</div>
							<div className="shoppingBasketCartSingleProductDeleteSection">
								<div className="shoppingBasketCartSingleProductDeleteSectionButton" onClick={() => handleDeleteItemFromCart(product.productId)}>
									<svg xmlns="http://www.w3.org/2000/svg" width="11.375" height="13.125" viewBox="0 0 11.375 13.125">
										<path
											id="trash-fill"
											d="M2.375.875A.875.875,0,0,0,1.5,1.75v.875a.875.875,0,0,0,.875.875h.438v7.875a1.75,1.75,0,0,0,1.75,1.75h5.25a1.75,1.75,0,0,0,1.75-1.75V3.5H12a.875.875,0,0,0,.875-.875V1.75A.875.875,0,0,0,12,.875H8.937A.875.875,0,0,0,8.062,0H6.312a.875.875,0,0,0-.875.875ZM5,4.375a.437.437,0,0,1,.438.437v6.125a.437.437,0,0,1-.875,0V4.812A.437.437,0,0,1,5,4.375Zm2.187,0a.438.438,0,0,1,.438.437v6.125a.438.438,0,0,1-.875,0V4.812A.437.437,0,0,1,7.187,4.375Zm2.625.437a.438.438,0,0,0-.875,0v6.125a.438.438,0,0,0,.875,0Z"
											transform="translate(-1.5)"
											fillRule="evenodd"
										/>
									</svg>
								</div>
							</div>
						</div>
					</div>
				</div>
			);
		},
		[settings, handleDecreaseQuantity, handleIncreaseQuantity, handleUpdateInputField, handleDeleteItemFromCart]
	);

	const renderListProduct = useCallback(
		(product: PrebasketProduct) => {
			const color = settings?.playerStyling?.primaryColor ?? '#126cfc';
			const primaryColor = { color: color };
			const primaryBGColor = { backgroundColor: tinycolor({ ...tinycolor(color).toRgb(), a: 0.2 }).toRgbString() };

			return (
				<div className="shoppingBasketListSingleProduct" key={product.prodId?.value}>
					<div className="shoppingBasketListSingleProductImageWrapper">
						<img className="shoppingBasketCartSingleProductImage" src={product.imageUrl?.value} alt={product.prodName?.value} />
					</div>
					<div className="shoppingBasketListSingleProductMiddleSection">
						<div className="shoppingBasketCartSingleProductMiddleSectionInfo">
							<a href={product.prodPage?.value} target="_blank" className="shoppingBasketCartSingleProductMiddleSectionTitle" rel="noreferrer">
								{product.prodName?.value}
							</a>
							<div className="shoppingBasketCartSingleProductMiddleSectionDes">{product.prodDescription?.value}</div>
							<div className="shoppingBasketCartSingleProductMiddleSectionPrice">{product.price?.value}</div>
						</div>
						<div
							className="shoppingBasketListSingleProductMiddleSectionViewButton"
							style={{ ...primaryColor, ...primaryBGColor }}
							onClick={() => handleViewProductButton(product)}
						>
							{t('VIEW_PRODUCT')}
						</div>
					</div>
				</div>
			);
		},
		[settings, handleViewProductButton, t]
	);

	const renderPrebasket = useCallback(
		(product: PrebasketProduct) => {
			if (!product.variantName1?.value && !product.variantName2?.value) {
				if (product.imageUrl?.value) {
					return (
						<>
							<div className="shoppingBasketPreBasketWrapper">
								<div className="shoppingBasketPreBasketSlideshow">
									<div className="shoppingBasketPreBasketSlideshowImages">
										<div>
											<img src={product.imageUrl.value} alt={product.prodName?.value} />
										</div>
									</div>
								</div>
								<div className="shoppingBasketPreBasketTitlePriceWrapper">
									<div className="shoppingBasketPreBasketTitle">{product.prodName?.value}</div>
									<div className="shoppingBasketPreBasketPrice">{product.price?.value}</div>
								</div>
								<div className="shoppingBasketPreBasketDes">{product.prodDescription?.value}</div>
							</div>
							<div
								className="shoppingBasketListSingleProductMiddleSectionViewButton"
								onClick={() => handlePrebasketAddToCart(product, '', '')}
							>
								{t('ADD_TO_CART')}
							</div>
						</>
					);
				}
				return null;
			}

			const images = product.variantCombinations?.value.map((item: VariantCombination) => ({
				url: item.comboImg === '' ? prebasketProduct.imageUrl?.value : item.comboImg,
			})) || [];

			const priceValue =
				product.variantCombinations?.value[currentComboIndex]?.comboPrice + ' ' + product.price?.value.split(' ')[1];

			const hasVariant1 = product.variantName1?.value !== '';
			const hasVariant2 = product.variantName2?.value !== '';

			return (
				<>
					<div className="shoppingBasketPreBasketWrapper">
						<div className="shoppingBasketPreBasketSlideshow">
							<div className="shoppingBasketPreBasketSlideshowImages">
								{images.map((img: any, index: number) => (
									<div
										key={index}
										className={index === 0 ? 'firstImage' : ''}
										style={index === 0 ? { marginLeft: getCurrentSlideMargin(currentSliderIndex) } : {}}
									>
										<img src={img.url} alt={`Product ${index}`} />
									</div>
								))}
							</div>
							<a className="prebasketSlideshowPrev" onClick={() => handleClickPrevSlider(currentSliderIndex, images.length)}>
								❮
							</a>
							<a className="prebasketSlideshowNext" onClick={() => handleClickNextSlider(currentSliderIndex, images.length)}>
								❯
							</a>
						</div>
						<div className="shoppingBasketPreBasketTitlePriceWrapper">
							<div className="shoppingBasketPreBasketTitle">{product.prodName?.value}</div>
							<div className="shoppingBasketPreBasketPrice">{priceValue}</div>
						</div>
						<div className="shoppingBasketPreBasketDes">{product.prodDescription?.value}</div>

						<div className="shoppingBasketPreBasketDropdownWrapper">
							{hasVariant1 && (
								<div className={hasVariant2 ? 'shoppingBasketPreBasketDropdownVariant' : 'shoppingBasketPreBasketDropdownVariantSingle'}>
									<div className="shoppingBasketPreBasketDropdownVariantName">{product.variantName1?.value}</div>
									<Dropdown
										className="shoppingBasketPreBasketDropdownVariantDropWrapper"
										menuClassName="shoppingBasketPreBasketDropdownMenu"
										placeholderClassName="shoppingBasketPreBasketDropdownPlaceholder"
										options={product.variantValues1?.value || []}
										onChange={(e: any) => setCurrentDropdownValue1(e.value)}
										value={currentDropdownValue1 || product.variantValues1?.value[0]}
										placeholder="Select an option"
									/>
								</div>
							)}

							{hasVariant2 && (
								<div className={hasVariant1 ? 'shoppingBasketPreBasketDropdownVariant' : 'shoppingBasketPreBasketDropdownVariantSingle'}>
									<div className="shoppingBasketPreBasketDropdownVariantName">{product.variantName2?.value}</div>
									<Dropdown
										className="shoppingBasketPreBasketDropdownVariantDropWrapper"
										menuClassName="shoppingBasketPreBasketDropdownMenu"
										placeholderClassName="shoppingBasketPreBasketDropdownPlaceholder"
										options={product.variantValues2?.value || []}
										onChange={(e: any) => setCurrentDropdownValue2(e.value)}
										value={currentDropdownValue2 || product.variantValues2?.value[0]}
										placeholder="Select an option"
									/>
								</div>
							)}
						</div>
					</div>

					<div
						className="shoppingBasketListSingleProductMiddleSectionViewButton"
						onClick={() => handlePrebasketAddToCart(product, currentDropdownValue1, currentDropdownValue2)}
					>
						{t('ADD_TO_CART')}
					</div>
				</>
			);
		},
		[
			currentComboIndex,
			currentDropdownValue1,
			currentDropdownValue2,
			currentSliderIndex,
			getCurrentSlideMargin,
			handleClickNextSlider,
			handleClickPrevSlider,
			handlePrebasketAddToCart,
			prebasketProduct,
			t,
		]
	);

	if (currentMedia?.custom?.['event-start']) {
		return null;
	}

	if (!settings?.engagementFeatures?.enabled || !settings?.engagementFeatures?.showShoppingCart) {
		return null;
	}

	const color = settings?.playerStyling?.primaryColor ?? '#126cfc';
	const primaryColor = { color: color };
	const primaryBGColor = { backgroundColor: tinycolor({ ...tinycolor(color).toRgb(), a: 0.2 }).toRgbString() };

	return (
		<div
			className={joinClassNames([
				'shoppingBasketContainer',
				isMobile ? 'shoppingBasketContainer--mobile' : '',
				showShoppingCart ? 'shoppingBasketContainer--visible' : '',
			])}
		>
			<div className="shoppingBasketTabWrapper">
				<div className="shoppingBasketBackButton" onClick={() => setShowShoppingCart(!showShoppingCart)}>
					<svg xmlns="http://www.w3.org/2000/svg" width="7.002" height="13.002" viewBox="0 0 7.002 13.002">
						<path
							id="chevron-down"
							d="M.147.147a.5.5,0,0,1,.708,0L6.5,5.794,12.147.147a.5.5,0,1,1,.708.708l-6,6a.5.5,0,0,1-.708,0l-6-6a.5.5,0,0,1,0-.708Z"
							transform="translate(0 13.002) rotate(-90)"
							fillRule="evenodd"
						/>
					</svg>
				</div>
				<div
					className={currentTab === 'cart' ? 'shoppingBasketCartButton highlightedTab' : 'shoppingBasketCartButton'}
					onClick={() => setCurrentTab('cart')}
				>
					{t('CART')}
				</div>

				<div
					style={{
						width: '0px',
						height: '20px',
						display: 'inline-block',
						border: '1px solid #E5E9EC',
						position: 'absolute',
						left: '50%',
					}}
				/>

				<div
					className={currentTab === 'list' ? 'shoppingBasketShoppingListButton highlightedTab' : 'shoppingBasketShoppingListButton'}
					onClick={() => setCurrentTab('list')}
				>
					{t('PRODUCT_LIST')}
				</div>
			</div>

			{currentTab === 'cart' ? (
				<div className="shoppingBasketCartWrapper">
					<div className="shoppingBasketCartProductList">
						<div className="shoppingBasketCartProductListWrapper">{cartArray.map((product) => renderCartProduct(product))}</div>
					</div>
					<div className="shoppingBasketCartTotalPrice">
						<div className="shoppingBasketCartTotalText">{t('TOTAL')}</div>
						{getTotalPrice(cartArray)}
					</div>
					<div className="shoppingBasketCartCheckoutButton" style={{ ...primaryColor, ...primaryBGColor }} onClick={() => handleCheckoutButton(cartArray)}>
						{t('GO_TO_CHECKOUT')}
					</div>
				</div>
			) : currentTab === 'list' ? (
				<div className="shoppingBasketCartWrapper">
					<div className="shoppingBasketCartAllProductList">
						<div className="shoppingBasketCartAllProductListWrapper">{listArray.map((product) => renderListProduct(product))}</div>
					</div>
				</div>
			) : (
				<div className="shoppingBasketPrebasket">{renderPrebasket(prebasketProduct)}</div>
			)}
		</div>
	);
};

export default ShoppingBasket;
